import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { AlertTriangle, KeyRound } from "lucide-react";

export default function Auth() {
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ username: "", identifier: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async () => {
    setBusy(true);
    try {
      let u;
      if (mode === "login") {
        u = await login(form.identifier, form.password);
      } else {
        u = await register(form.username, form.email, form.password);
      }
      toast.success("Welcome to Tradeable Exchange!");
      if (u?.role === "admin" || u?.is_member) {
        navigate("/dashboard");
      } else {
        navigate("/membership");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Authentication failed");
    }
    setBusy(false);
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const inputCls = "w-full mt-1 p-3 nb-border rounded-xl font-medium outline-none focus:ring-2 focus:ring-[#E6B31E]";

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-5 py-10">
      <div className="w-full max-w-md bg-white nb-border rounded-2xl p-8 nb-shadow-lg">
        <div className="flex flex-col items-center mb-6">
          <img src="/logo.png" alt="Tradeable Exchange" className="w-20 h-20 rounded-2xl object-cover mb-3" />
          <h1 className="font-display font-black text-2xl tracking-tight text-center">
            {mode === "login" ? "Log in to trade" : "Create your account"}
          </h1>
          <p className="text-sm text-black/50 font-medium">Tradeable Exchange</p>
        </div>

        <div className="space-y-4">
          {mode === "register" ? (
            <>
              <div>
                <label className="text-sm font-bold">Username</label>
                <input data-testid="username-field" value={form.username} onChange={set("username")} placeholder="jane_trader" className={inputCls} />
              </div>
              <div>
                <label className="text-sm font-bold">Email</label>
                <input data-testid="email-field" type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" className={inputCls} />
              </div>
            </>
          ) : (
            <div>
              <label className="text-sm font-bold">Email or username</label>
              <input data-testid="identifier-field" value={form.identifier} onChange={set("identifier")} placeholder="username or you@email.com" className={inputCls} />
            </div>
          )}
          <div>
            <label className="text-sm font-bold">Password</label>
            <input data-testid="password-field" type="password" value={form.password} onChange={set("password")} onKeyDown={(e) => e.key === "Enter" && submit()} placeholder="••••••••" className={inputCls} />
          </div>

          {mode === "register" && (
            <div data-testid="no-recovery-warning" className="flex items-start gap-2 bg-[#FBF7E9] nb-border rounded-xl p-3 text-xs font-semibold text-black/70">
              <KeyRound size={16} strokeWidth={3} className="text-[#B8860B] flex-shrink-0 mt-0.5" />
              <span>We store your <b>username</b> — never your raw password. There is <b>no password recovery</b>. If you forget it, your account cannot be restored, so keep it safe.</span>
            </div>
          )}
          {mode === "login" && (
            <div data-testid="no-recovery-warning" className="flex items-start gap-2 bg-[#FBF7E9] nb-border rounded-xl p-3 text-xs font-semibold text-black/70">
              <KeyRound size={16} strokeWidth={3} className="text-[#B8860B] flex-shrink-0 mt-0.5" />
              <span>Reminder: there is <b>no password recovery</b>. If you forget your password, your account cannot be restored.</span>
            </div>
          )}

          <button data-testid="auth-submit-btn" disabled={busy} onClick={submit}
            className="w-full bg-black text-[#E6B31E] font-extrabold text-lg rounded-full py-3.5 disabled:opacity-50">
            {busy ? "Please wait…" : mode === "login" ? "Log in" : "Sign up"}
          </button>

          <div className="flex items-center gap-3 text-xs font-bold text-black/40">
            <div className="flex-1 h-px bg-black/10" /> OR <div className="flex-1 h-px bg-black/10" />
          </div>

          <button data-testid="google-btn" onClick={google}
            className="w-full flex items-center justify-center gap-2 bg-white text-black font-bold rounded-full py-3 nb-border hover:bg-[#FBF7E9] transition-colors">
            <img src="https://www.google.com/favicon.ico" alt="" className="w-4 h-4" /> Continue with Google
          </button>
        </div>

        <div className="text-center text-sm font-semibold mt-5">
          {mode === "login" ? "New here?" : "Already have an account?"}{" "}
          <button data-testid="toggle-mode-btn" onClick={() => setMode(mode === "login" ? "register" : "login")} className="text-[#B8860B] font-extrabold underline">
            {mode === "login" ? "Create an account" : "Log in"}
          </button>
        </div>

        <div data-testid="rules-warning" className="mt-6 bg-black text-white rounded-xl p-4 border-2 border-[#E6B31E]">
          <div className="flex items-center gap-2 font-display font-extrabold text-[#E6B31E] text-sm">
            <AlertTriangle size={16} strokeWidth={3} /> RULES WARNING
          </div>
          <p className="text-xs text-white/80 font-medium mt-2 leading-relaxed">
            Nothing can be sold or traded that is against the true laws and that damages life.
            Users who break this rule will be <span className="text-[#E6B31E] font-extrabold">permanently banned</span>.
          </p>
        </div>
      </div>
    </div>
  );
}
